import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { docsForModule, openPlaybook } from '../../config/helpDocs';

const STEPS = [
  ['Pick the Client', 'Who are we watching for? Choose the company name.'],
  ['Pick the Site', 'Which of their places is this about? The list only shows that client\'s sites.'],
  ['Pick the Activity Type', 'What kind of thing happened — alarm, camera check, police called?'],
  ['Quick Comment (optional)', 'Picks common wording for you. You can still change the words after.'],
  ['Write what happened', 'What you saw, what you did, who you called. Plain words are fine.'],
  ['Work Order # / Account #', 'Only if you have one. These stay inside TriCorps — the client never sees them.'],
  ['Press Submit Entry', 'Or hold Ctrl and press Enter. The time is stamped by the server, not by you.'],
];

/** "?" on the operator entry console. Quick reminders first, then the playbook. */
export function EntryHelp() {
  const [open, setOpen] = useState(false);
  const docs = docsForModule('gsoc');

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
              title="How to log an entry" aria-label="Help — how to log an entry"
              data-testid="gsoc-entry-help-btn">
        <HelpCircle className="w-4 h-4" aria-hidden="true" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>How to log an entry</SheetTitle>
          </SheetHeader>

          <ol className="mt-5 space-y-3" data-testid="gsoc-entry-help-steps">
            {STEPS.map(([title, body], i) => (
              <li key={title} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-surface-sunken border border-line
                                 text-xs font-semibold text-fg flex items-center justify-center">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-fg">{title}</p>
                  <p className="text-sm text-fg-muted">{body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-5 rounded-md border border-warning-border bg-warning-bg p-3 text-sm">
            <p className="font-semibold text-fg">Made a mistake?</p>
            <p className="text-fg-secondary mt-0.5">
              Entries can never be edited or deleted. Open the Activity Log, find the entry, and press
              <strong> File Correction</strong>. Both entries stay on the record.
            </p>
          </div>

          <div className="mt-5 space-y-2">
            <Link to="/gsoc/sop" onClick={() => setOpen(false)}
                  className="block text-sm text-brand hover:underline" data-testid="gsoc-entry-help-sop-link">
              Open the SOP reminders →
            </Link>
            <Link to="/gsoc/contacts" onClick={() => setOpen(false)}
                  className="block text-sm text-brand hover:underline" data-testid="gsoc-entry-help-contacts-link">
              Look up a contact or call tree →
            </Link>
            <Link to="/gsoc/pass-on" onClick={() => setOpen(false)}
                  className="block text-sm text-brand hover:underline" data-testid="gsoc-entry-help-passon-link">
              Re-read the Pass-On notes →
            </Link>
          </div>

          <div className="mt-5 space-y-3">
            {docs.map((d) => (
              <div key={d.id} className="border border-line rounded-lg p-4 bg-surface"
                   data-testid={`gsoc-entry-help-doc-${d.id}`}>
                <h4 className="font-semibold text-fg mb-1 leading-snug">{d.title}</h4>
                <p className="text-sm text-fg-muted mb-3">{d.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-fg-muted">Updated {d.updated}</span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" title="Open in new tab"
                      onClick={() => openPlaybook(d)}>
                      <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                    <Button size="sm" onClick={() => openPlaybook(d, { download: true })}>
                      <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> PDF
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
